import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { User, Calendar, Scale, Utensils, MessageSquare, Copy, Plus, Minus, Upload, Eye, Trash2, Edit, FileText, AlertTriangle, ShoppingCart, RefreshCw, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import Navbar from '@/components/Navbar';
import GroceryListModal from '@/components/GroceryListModal';
import CoachMealPlanView from '@/components/CoachMealPlanView';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix image URLs from other preview environments to use current backend
const fixImageUrl = (url) => {
  if (!url) return null;
  // Skip data URLs
  if (url.startsWith('data:')) return url;
  // If URL contains any emergentagent domain OR getmealguide domain, normalize to current BACKEND_URL
  if (url.includes('emergentagent.com/api/') || url.includes('getmealguide.com/api/')) {
    const pathMatch = url.match(/\/api\/uploads\/.+$/);
    if (pathMatch) {
      return `${BACKEND_URL}${pathMatch[0]}`;
    }
  }
  return url;
};

export default function ClientProfile({ user, onLogout }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [weightLogs, setWeightLogs] = useState([]);
  const [mealPlan, setMealPlan] = useState(null);
  const [allMealPlans, setAllMealPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [onboardingResponse, setOnboardingResponse] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityLog, setActivityLog] = useState({ last_active: null, entries: [] });
  const [activityLoading, setActivityLoading] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [editRecipeData, setEditRecipeData] = useState(null);
  const [foodBankItems, setFoodBankItems] = useState([]);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swappingRecipe, setSwappingRecipe] = useState(null);
  const [swapOptions, setSwapOptions] = useState([]);
  const [loadingSwapOptions, setLoadingSwapOptions] = useState(false);
  
  // Add Ingredient state
  const [showAddIngredient, setShowAddIngredient] = useState(false);
  const [newIngredientFoodId, setNewIngredientFoodId] = useState('');
  const [newIngredientGrams, setNewIngredientGrams] = useState('100');
  const [adding, setAdding] = useState(false);

  // Grocery List state
  const [selectedRecipes, setSelectedRecipes] = useState(new Set());
  const [showGroceryList, setShowGroceryList] = useState(false);

  // Calorie adjustment level state
  const [adjustmentLevel, setAdjustmentLevel] = useState(0);

  // Onboarding prompt dialog state
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);

  // Regenerate Meal Plan state
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateParams, setRegenerateParams] = useState({
    meals_per_day: '3',
    diet_type: 'standard',
    distribution_profile: 'balanced',
    target_calories: null  // null = auto-calculate from TDEE
  });

  // When dialog opens, pre-fill with current meal plan values
  useEffect(() => {
    if (regenerateDialogOpen && mealPlan) {
      console.log('🔄 Pre-filling regenerate params:', {
        meals_per_day: mealPlan.meals_per_day,
        diet_type: mealPlan.diet_type,
        distribution: mealPlan.distribution_profile,
        target_calories: mealPlan.target_calories
      });
      
      setRegenerateParams({
        meals_per_day: String(mealPlan.meals_per_day || 3),
        diet_type: mealPlan.diet_type || 'standard',
        distribution_profile: mealPlan.distribution_profile || 'balanced',
        target_calories: Math.round(mealPlan.target_calories || 0)
      });
    }
  }, [regenerateDialogOpen, mealPlan]);

  useEffect(() => {
    fetchClientData();
    fetchFoodBank();
    fetchActivityLog();
  }, [clientId]);

  const fetchActivityLog = async () => {
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API}/coach/clients/${clientId}/activity-log`);
      setActivityLog(res.data || { last_active: null, entries: [] });
    } catch (error) {
      console.log('Failed to load activity log');
    } finally {
      setActivityLoading(false);
    }
  };

  const timeAgo = (isoString) => {
    if (!isoString) return 'Never';
    const then = new Date(isoString);
    const diffMs = Date.now() - then.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const fetchClientData = async () => {
    try {
      const [clientRes, checkinsRes, weightRes, allPlansRes] = await Promise.all([
        axios.get(`${API}/coach/clients`).then(res => res.data.find(c => c.id === clientId)),
        axios.get(`${API}/checkins`, { params: { user_id: clientId } }),
        axios.get(`${API}/weight-logs`, { params: { user_id: clientId } }),
        axios.get(`${API}/meal-plans/${clientId}/all`)
      ]);

      setClient(clientRes);
      setCheckins(checkinsRes.data || []);
      
      // Sort weight logs by date (most recent first)
      const sortedWeightLogs = (weightRes.data || []).sort((a, b) => 
        new Date(b.date || b.created_at) - new Date(a.date || a.created_at)
      );
      setWeightLogs(sortedWeightLogs);
      
      setAllMealPlans(allPlansRes.data || []);
      
      // Set the currently selected plan or first one
      const selectedPlan = allPlansRes.data?.find(p => p.currently_selected) || allPlansRes.data?.[0];
      if (selectedPlan) {
        setMealPlan(selectedPlan);
        setSelectedPlanId(selectedPlan.id);
      }
      
      // Fetch onboarding and subscription
      try {
        const onboardingRes = await axios.get(`${API}/forms/onboarding/responses/${clientId}`);
        setOnboardingResponse(onboardingRes.data);
      } catch (e) {
        console.log('No onboarding response');
      }
      
      try {
        const subRes = await axios.get(`${API}/coach/subscriptions/${clientId}`);
        setSubscription(subRes.data);
      } catch (e) {
        console.log('No subscription');
      }
    } catch (error) {
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFoodBank = async () => {
    try {
      const response = await axios.get(`${API}/food-bank?limit=500`);
      const items = response.data.foods || response.data || [];
      setFoodBankItems(items);
    } catch (error) {
      console.error('Failed to fetch Food Bank:', error);
    }
  };

  // Enrich recipe ingredients with food_bank_id by looking up names
  const enrichRecipeWithFoodBank = async (recipe) => {
    const enrichedIngredients = await Promise.all(
      recipe.ingredients.map(async (ing) => {
        // If already has food_bank_id, return as is
        if (ing.food_bank_id) {
          return ing;
        }

        // Try to find matching Food Bank item by name
        try {
          const searchName = ing.food_item_name || ing.food_bank_name || '';
          if (!searchName) return ing;

          // Try exact match first
          let foodItem = foodBankItems.find(
            item => item.name.toLowerCase() === searchName.toLowerCase()
          );

          // If no exact match, try API search
          if (!foodItem) {
            const response = await axios.get(`${API}/food-bank/search/${encodeURIComponent(searchName)}`);
            if (response.data && response.data.id) {
              foodItem = response.data;
            }
          }

          if (foodItem) {
            return {
              ...ing,
              food_bank_id: foodItem.id,
              food_bank_name: foodItem.name
            };
          }
        } catch (error) {
          console.error(`Failed to find Food Bank item for ${ing.food_item_name}:`, error);
        }

        // Return original if lookup failed
        return ing;
      })
    );

    return {
      ...recipe,
      ingredients: enrichedIngredients
    };
  };

  const handleLevelAdjustment = async (level) => {
    if (level === 0) {
      toast.error('Please select a non-zero adjustment level');
      return;
    }

    setAdjusting(true);

    try {
      const response = await axios.post(`${API}/meal-plans/${clientId}/adjust-calories`, null, {
        params: { adjustment_level: level }
      });

      const { old_calories, new_calories, calorie_change } = response.data;
      
      toast.success(
        `Level ${level > 0 ? '+' : ''}${level} applied! ${Math.round(old_calories)} → ${Math.round(new_calories)} kcal (${calorie_change > 0 ? '+' : ''}${Math.round(calorie_change)} kcal)`
      );
      
      setAdjustmentLevel(0);
      await fetchClientData();  // Refresh to show updated values
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to adjust meal plan';
      toast.error(errorMsg);
    } finally {
      setAdjusting(false);
    }
  };

  const handleRegenerateMealPlan = async () => {
    setRegenerating(true);
    try {
      await axios.post(`${API}/regenerate-meal-plan/${clientId}`, regenerateParams);
      const successMessage = mealPlan ? 'Meal plan regenerated successfully!' : 'Meal plan assigned successfully!';
      toast.success(successMessage);
      setRegenerateDialogOpen(false);
      fetchClientData();
    } catch (error) {
      console.error('Regenerate error:', error);
      const errorMessage = mealPlan ? 'Failed to regenerate meal plan' : 'Failed to assign meal plan';
      toast.error(error.response?.data?.detail || errorMessage);
    } finally {
      setRegenerating(false);
    }
  };

  const handleGeneratePrompt = async () => {
    try {
      const response = await axios.get(`${API}/onboarding-prompt/${clientId}`);
      setGeneratedPrompt(response.data.prompt);
      setPromptDialogOpen(true);
    } catch (error) {
      console.error('Prompt generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate prompt');
    }
  };

  const handleImageUpload = async (recipeId) => {
    const imageUrl = prompt('Enter image URL for this recipe:');
    if (!imageUrl) return;

    try {
      await axios.patch(`${API}/recipes/${recipeId}/image`, { image_url: imageUrl });
      toast.success('Recipe image updated!');
      fetchClientData();
    } catch (error) {
      toast.error('Failed to update image');
    }
  };

  // Update ingredient grams via API (Client Accuracy feature)
  const updateIngredientGrams = async (ingredientIndex, newGrams) => {
    try {
      const ingredient = editRecipeData.ingredients[ingredientIndex];
      const gramsValue = parseFloat(newGrams);
      
      if (isNaN(gramsValue) || gramsValue <= 0) {
        toast.error('Please enter a valid positive number for grams');
        return;
      }
      
      // For meal plan recipes, we need food_bank_id
      if (!ingredient.food_bank_id) {
        toast.error('This ingredient is missing Food Bank reference. Please swap it with a Food Bank item.');
        return;
      }
      
      const response = await axios.put(
        `${API}/recipes/${editingRecipe}/ingredients/${ingredientIndex}`,
        {
          food_bank_id: ingredient.food_bank_id,
          grams: gramsValue
        }
      );

      // Update local state
      const updatedRecipeData = { ...editRecipeData };
      updatedRecipeData.ingredients[ingredientIndex] = response.data.ingredient;
      
      // Update recipe totals if provided
      if (response.data.recipe_totals) {
        updatedRecipeData.total_kcal = response.data.recipe_totals.kcal;
        updatedRecipeData.total_protein = response.data.recipe_totals.protein_g;
        updatedRecipeData.total_carbs = response.data.recipe_totals.carbs_g;
        updatedRecipeData.total_fat = response.data.recipe_totals.fat_g;
      }
      
      setEditRecipeData(updatedRecipeData);

    } catch (error) {
      console.error('Update ingredient error:', error);
      
      let errorMessage = 'Failed to update ingredient';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message || String(e)).join(', ');
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Swap ingredient with Food Bank item
  const swapIngredient = async (ingredientIndex, newFoodBankId) => {
    try {
      const currentIngredient = editRecipeData.ingredients[ingredientIndex];
      const currentGrams = currentIngredient.grams || 100;

      const response = await axios.put(
        `${API}/recipes/${editingRecipe}/ingredients/${ingredientIndex}`,
        {
          food_bank_id: newFoodBankId,
          grams: currentGrams
        }
      );

      // Update local state
      const updatedRecipeData = { ...editRecipeData };
      updatedRecipeData.ingredients[ingredientIndex] = response.data.ingredient;
      updatedRecipeData.allergens = response.data.allergens || editRecipeData.allergens || [];
      
      if (response.data.recipe_totals) {
        updatedRecipeData.total_kcal = response.data.recipe_totals.kcal;
        updatedRecipeData.total_protein = response.data.recipe_totals.protein_g;
        updatedRecipeData.total_carbs = response.data.recipe_totals.carbs_g;
        updatedRecipeData.total_fat = response.data.recipe_totals.fat_g;
      }
      
      setEditRecipeData(updatedRecipeData);
      toast.success('Ingredient swapped successfully');

    } catch (error) {
      console.error('Swap ingredient error:', error);
      toast.error('Failed to swap ingredient');
    }
  };

  const handleSaveRecipe = async () => {
    try {
      await axios.patch(`${API}/recipes/${editingRecipe}`, editRecipeData);
      toast.success('Recipe updated!');
      setEditingRecipe(null);
      setEditRecipeData(null);
      fetchClientData();
    } catch (error) {
      toast.error('Failed to update recipe');
    }
  };

  const handleAddIngredient = async () => {
    if (!newIngredientFoodId || !newIngredientGrams) {
      toast.error('Please select an ingredient and enter grams');
      return;
    }

    const gramsValue = parseFloat(newIngredientGrams);
    if (isNaN(gramsValue) || gramsValue <= 0) {
      toast.error('Please enter a valid positive number for grams');
      return;
    }

    setAdding(true);
    try {
      const response = await axios.post(
        `${API}/recipes/${editingRecipe}/ingredients`,
        {
          food_bank_id: newIngredientFoodId,
          grams: gramsValue
        }
      );

      // Update recipe with new ingredient, allergens, and totals
      const updatedRecipeData = { ...editRecipeData };
      updatedRecipeData.ingredients.push(response.data.ingredient);
      updatedRecipeData.allergens = response.data.allergens || editRecipeData.allergens || [];
      
      // Recalculate totals from all ingredients
      updatedRecipeData.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipeData.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipeData.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipeData.total_fat = response.data.recipe_totals.fat_g;
      
      setEditRecipeData(updatedRecipeData);
      setShowAddIngredient(false);
      setNewIngredientFoodId('');
      setNewIngredientGrams('100');
      
      toast.success('Ingredient added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add ingredient');
    } finally {
      setAdding(false);
    }
  };

  const deleteIngredient = async (ingredientIndex) => {
    try {
      const response = await axios.delete(
        `${API}/recipes/${editingRecipe}/ingredients/${ingredientIndex}`
      );

      // Update recipe with new totals and allergens
      const updatedRecipeData = { ...editRecipeData };
      updatedRecipeData.ingredients.splice(ingredientIndex, 1);
      updatedRecipeData.allergens = response.data.allergens || editRecipeData.allergens || [];
      updatedRecipeData.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipeData.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipeData.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipeData.total_fat = response.data.recipe_totals.fat_g;
      
      setEditRecipeData(updatedRecipeData);
      toast.success(`Removed ${response.data.removed_ingredient}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove ingredient');
    }
  };

  const handleOpenSwapDialog = async (recipe) => {
    setSwappingRecipe(recipe);
    setSwapDialogOpen(true);
    setLoadingSwapOptions(true);
    
    try {
      const response = await axios.get(`${API}/recipes/${recipe.id}/swap-options`);
      setSwapOptions(response.data.compatible_recipes || []);
    } catch (error) {
      console.error('Error fetching swap options:', error);
      toast.error('Failed to load swap options');
      setSwapOptions([]);
    } finally {
      setLoadingSwapOptions(false);
    }
  };

  const handleSwapRecipe = async (newRecipeId) => {
    try {
      await axios.put(`${API}/recipes/${swappingRecipe.id}/swap`, { new_recipe_id: newRecipeId });
      toast.success('Recipe swapped successfully!');
      setSwapDialogOpen(false);
      setSwappingRecipe(null);
      fetchClientData(); // Refresh meal plan
    } catch (error) {
      console.error('Error swapping recipe:', error);
      toast.error('Failed to swap recipe');
    }
  };

  const [generatingAI, setGeneratingAI] = useState(false);

  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    try {
      const response = await axios.post(`${API}/generate-meal-plan/${clientId}`);
      
      if (response.data.status === 'success') {
        toast.success(`Meal plan generated and saved! ${response.data.recipes_count} recipes created.`);
        // Refresh client data to show new meal plan
        fetchClientData();
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(`Generation failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleMealPlanUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingCSV(true);

    try {
      const text = await file.text();
      
      // Detect if it's JSON or CSV
      const trimmed = text.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        // It's JSON
        await handleJSONUpload(text);
      } else {
        // It's CSV
        await handleCSVUpload(text);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Upload failed: ${error.message}`);
      setUploadingCSV(false);
    }
  };

  // Convert ChatGPT's preferred format to our required format
  const convertChatGPTFormat = (chatgptData) => {
    const converted = {
      meal_plan_name: chatgptData.meal_plan_name || "Meal Plan",
      target_calories: chatgptData.target_calories || chatgptData.client_information?.recommended_calories || 2000,
      recipes: [],
      grocery_list: chatgptData.grocery_list || []
    };

    // Handle nested meals structure like { meals: { breakfast: [...], lunch: [...] } }
    if (chatgptData.meals && typeof chatgptData.meals === 'object') {
      console.log('Found nested meals structure');
      Object.entries(chatgptData.meals).forEach(([mealType, recipesArray]) => {
        if (Array.isArray(recipesArray)) {
          recipesArray.forEach(recipe => {
            const convertedRecipe = convertRecipeFormat(recipe, mealType);
            if (convertedRecipe) {
              converted.recipes.push(convertedRecipe);
            }
          });
        }
      });
    }

    // Handle flat recipes array (but might have wrong field names/formats)
    if (chatgptData.recipes && Array.isArray(chatgptData.recipes)) {
      console.log('Found flat recipes array, converting formats...');
      chatgptData.recipes.forEach(recipe => {
        const convertedRecipe = convertRecipeFormat(recipe, recipe.meal_type);
        if (convertedRecipe) {
          converted.recipes.push(convertedRecipe);
        }
      });
    }

    console.log(`Converted ${converted.recipes.length} recipes`);
    return converted;
  };

  const convertRecipeFormat = (recipe, mealType) => {
    if (!recipe) return null;

    // Capitalize meal type properly
    const capitalizedMealType = (recipe.meal_type || mealType || 'Meal')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    // Convert ChatGPT recipe format to our format
    const converted = {
      meal_type: capitalizedMealType,
      name: recipe.name || recipe.recipe_name || 'Unnamed Recipe',
      estimated_time: recipe.estimated_time || (recipe.time_minutes ? `${recipe.time_minutes} mins` : null) || recipe.cooking_time || '15 mins',
      instructions: '',
      ingredients: []
    };

    // Handle instructions (array or string)
    if (Array.isArray(recipe.instructions)) {
      converted.instructions = recipe.instructions.map((step, i) => `${i + 1}. ${step}`).join(' ');
    } else if (typeof recipe.instructions === 'string') {
      converted.instructions = recipe.instructions;
    } else if (Array.isArray(recipe.steps)) {
      converted.instructions = recipe.steps.map((step, i) => `${i + 1}. ${step}`).join(' ');
    } else {
      converted.instructions = 'No instructions provided.';
    }

    // Handle ingredients (string array or object array)
    if (!recipe.ingredients || !Array.isArray(recipe.ingredients)) {
      console.warn('Recipe has no ingredients array:', recipe.name);
      return null;
    }

    recipe.ingredients.forEach((ing, index) => {
      if (typeof ing === 'string') {
        // Parse string ingredient like "1 avocado" or "2 tbsp almond butter"
        const parsed = parseIngredientString(ing, recipe.calories || 0, recipe.macros);
        converted.ingredients.push(parsed);
      } else if (typeof ing === 'object' && ing !== null) {
        // Already an object, convert field names
        converted.ingredients.push({
          food_item_name: ing.food_item || ing.item || ing.name || ing.ingredient || `Ingredient ${index + 1}`,
          count_display: ing.amount || ing.quantity || ing.count_display || '',
          grams: parseFloat(ing.grams || ing.weight_g || ing.weight || 0) || 100,
          kcal: parseFloat(ing.kcal || ing.calories || ing.cal || 0) || 0,
          protein_g: parseFloat(ing.protein_g || ing.protein || 0) || 0,
          carbs_g: parseFloat(ing.carbs_g || ing.carbs || ing.carb_g || 0) || 0,
          fat_g: parseFloat(ing.fat_g || ing.fat || 0) || 0
        });
      }
    });

    if (converted.ingredients.length === 0) {
      console.warn('Recipe has no valid ingredients after parsing:', recipe.name);
      return null;
    }

    return converted;
  };

  const parseIngredientString = (str, recipeCalories, recipeMacros) => {
    // Parse strings like "1 avocado", "2 tbsp almond butter", "6 oz ground beef"
    // Extract food name (everything after numbers/units)
    const parts = str.trim().split(/\s+/);
    let foodName = str.trim();
    let amount = '';
    
    // Try to extract amount and food name
    if (parts.length > 1) {
      // First part is likely number
      amount = parts.slice(0, 2).join(' '); // "1 cup" or "2 tbsp"
      foodName = parts.slice(2).join(' '); // rest is food name
    }
    
    if (!foodName) foodName = parts[parts.length - 1];

    // Estimate macros (very rough - divide recipe macros by ingredient count)
    const estimatedKcal = recipeCalories ? Math.round(recipeCalories / 5) : 50;
    const estimatedProtein = recipeMacros?.protein_g ? Math.round(recipeMacros.protein_g / 5) : 5;
    const estimatedCarbs = recipeMacros?.carb_g ? Math.round(recipeMacros.carb_g / 5) : 5;
    const estimatedFat = recipeMacros?.fat_g ? Math.round(recipeMacros.fat_g / 5) : 5;

    return {
      food_item_name: foodName,
      count_display: amount,
      grams: 100, // Default estimate
      kcal: estimatedKcal,
      protein_g: estimatedProtein,
      carbs_g: estimatedCarbs,
      fat_g: estimatedFat
    };
  };

  const handleJSONUpload = async (text) => {
    try {
      // Try to extract JSON if it's wrapped in code blocks or explanatory text
      let jsonText = text.trim();
      
      // Remove markdown code blocks
      if (jsonText.includes('```json')) {
        const match = jsonText.match(/```json\s*([\s\S]*?)\s*```/);
        if (match) {
          jsonText = match[1];
        }
      } else if (jsonText.includes('```')) {
        const match = jsonText.match(/```\s*([\s\S]*?)\s*```/);
        if (match) {
          jsonText = match[1];
        }
      }
      
      // Try to find JSON object if wrapped in text
      if (!jsonText.startsWith('{')) {
        const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          jsonText = jsonMatch[0];
        }
      }
      
      jsonText = jsonText.trim();
      
      // Debug log
      console.log('Attempting to parse JSON:', jsonText.substring(0, 200) + '...');
      
      let data = JSON.parse(jsonText);
      
      // ChatGPT often ignores structure or uses wrong format - always convert
      console.log('Converting ChatGPT format...', data);
      data = convertChatGPTFormat(data);
      
      // Validate JSON structure with detailed error messages
      if (!data.recipes || !Array.isArray(data.recipes)) {
        throw new Error('Invalid JSON: "recipes" field is missing or not an array after conversion. Check the JSON structure.');
      }
      
      if (!Array.isArray(data.recipes)) {
        throw new Error('Invalid JSON: "recipes" must be an array, not ' + typeof data.recipes);
      }
      
      if (data.recipes.length === 0) {
        throw new Error('Invalid JSON: "recipes" array is empty. Need at least 1 recipe.');
      }

      const recipes = {};
      const groceryList = data.grocery_list || [];
      let totalCalories = 0;

      // Process recipes with detailed validation
      data.recipes.forEach((recipe, index) => {
        const missingFields = [];
        if (!recipe.meal_type) missingFields.push('meal_type');
        if (!recipe.name) missingFields.push('name');
        if (!recipe.ingredients) missingFields.push('ingredients');
        
        if (missingFields.length > 0) {
          throw new Error(`Recipe ${index + 1} ("${recipe.name || 'unnamed'}") is missing: ${missingFields.join(', ')}. Check the JSON structure.`);
        }
        
        if (!Array.isArray(recipe.ingredients)) {
          throw new Error(`Recipe ${index + 1} ("${recipe.name}"): ingredients must be an array`);
        }
        
        if (recipe.ingredients.length === 0) {
          throw new Error(`Recipe ${index + 1} ("${recipe.name}"): ingredients array is empty`);
        }

        const recipeKey = `${recipe.meal_type}_${recipe.name}`;
        recipes[recipeKey] = {
          day: null,
          meal_type: recipe.meal_type,
          name: recipe.name,
          instructions: recipe.instructions || '',
          estimated_time: recipe.estimated_time || '',
          image_url: null,
          ingredients: []
        };

        // Process ingredients
        recipe.ingredients.forEach(ing => {
          recipes[recipeKey].ingredients.push({
            food_item_name: ing.food_item,
            count_display: ing.amount || '',
            grams: parseFloat(ing.grams) || 0,
            kcal: parseFloat(ing.kcal) || 0,
            protein_g: parseFloat(ing.protein_g) || 0,
            carbs_g: parseFloat(ing.carbs_g) || 0,
            fat_g: parseFloat(ing.fat_g) || 0
          });

          totalCalories += parseFloat(ing.kcal) || 0;
        });
      });

      const recipesArray = Object.values(recipes);
      
      console.log('Parsed JSON recipes:', recipesArray.length);
      console.log('Grocery list items:', groceryList.length);

      // Use recommended calories from onboarding
      let targetCalories = 2000;
      
      if (onboardingResponse && onboardingResponse.answers_json) {
        const answers = onboardingResponse.answers_json;
        const age = parseInt(answers.age) || 25;
        const weight = parseFloat(answers.weight) || 70;
        const height = parseFloat(answers.height) || 170;
        const gender = answers.gender || 'Male';
        const activityLevel = answers.activity_level || 'Moderate';
        const goal = answers.goal || 'Maintenance';
        
        let bmr;
        if (gender === 'Male') {
          bmr = 10 * weight + 6.25 * height - 5 * age + 5;
        } else {
          bmr = 10 * weight + 6.25 * height - 5 * age - 161;
        }
        
        const activityMultipliers = {
          'Sedentary': 1.2,
          'Light': 1.375,
          'Moderate': 1.55,
          'Very Active': 1.725,
          'Extra Active': 1.9
        };
        
        const tdee = bmr * (activityMultipliers[activityLevel] || 1.55);
        
        let recommendedCalories = tdee;
        if (goal === 'Weight Loss') {
          recommendedCalories = tdee * 0.85;
        } else if (goal === 'Weight Gain') {
          recommendedCalories = tdee * 1.1;
        }
        
        targetCalories = Math.round(recommendedCalories);
        console.log(`Using onboarding recommended calories: ${targetCalories} kcal/day`);
        toast.success(`Target calories set to ${targetCalories} kcal/day from onboarding`);
      } else {
        console.log('No onboarding data found, using default 2000 kcal');
        toast.info('Using default 2000 kcal (complete onboarding for personalized calories)');
      }

      // Upload to backend
      const response = await axios.post(`${API}/meal-plans/${clientId}`, {
        recipes: recipesArray,
        target_calories: targetCalories,
        grocery_list: groceryList
      });

      toast.success(`Meal plan uploaded! ${recipesArray.length} recipes added.`);
      fetchClientData();
    } catch (error) {
      console.error('JSON parsing error:', error);
      console.error('Error stack:', error.stack);
      toast.error(`JSON upload failed: ${error.message}`);
    } finally {
      setUploadingCSV(false);
    }
  };

  const handleCSVUpload = async (text) => {
    try {
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or invalid');
        setUploadingCSV(false);
        return;
      }

      console.log('CSV Lines:', lines.length);
      console.log('First line:', lines[0]);

      // Parse CSV - handle quoted values
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };

      // Clean count_display for eggs and countable items
      const cleanCountDisplay = (countDisplay, ingredientName) => {
        const nameLower = ingredientName.toLowerCase();
        const displayLower = countDisplay.toLowerCase();
        
        // Check if it's eggs or countable items
        const countableItems = [
          'egg', 'banana', 'apple', 'orange', 'pear', 'peach', 'plum', 'mango', 'kiwi',
          'avocado', 'tomato', 'potato', 'onion', 'pepper', 'carrot',
          'piece', 'breast', 'fillet', 'slice', 'patty', 'sausage', 'drumstick', 'thigh'
        ];
        
        const isCountable = countableItems.some(item => 
          nameLower.includes(item) || displayLower.includes(item)
        );
        
        if (isCountable) {
          // Remove gram display from countable items
          // Extract just the number and unit, remove (XXXg) part
          return countDisplay.replace(/\s*\(\d+\.?\d*g\)/g, '').trim();
        }
        
        return countDisplay;
      };

      const recipes = {};
      const groceryList = [];
      let totalCalories = 0;
      const errors = [];
      let parsingGroceryList = false;

      for (let i = 1; i < lines.length; i++) {
        try {
          const line = lines[i].trim();
          
          // Skip empty lines
          if (!line) {
            continue;
          }
          
          // Detect grocery list section header - be very explicit
          const lineLower = line.toLowerCase();
          if (lineLower.includes('category,item,total_amount') || 
              lineLower.includes('category,item,amount') ||
              lineLower.includes('=== grocery') ||
              lineLower.includes('grocery list') ||
              lineLower === 'category,item,total_amount') {
            console.log('Found grocery list header:', line);
            parsingGroceryList = true;
            continue;
          }

          const values = parseCSVLine(line);

          // Parse grocery list section
          if (parsingGroceryList) {
            // Grocery list should have exactly 3 columns
            if (values.length === 3) {
              // Only add if it looks like actual grocery data (not empty or malformed)
              if (values[0] && values[1] && values[2]) {
                groceryList.push({
                  category: values[0],
                  item: values[1],
                  total_amount: values[2]
                });
              }
            }
            continue; // Skip everything else when in grocery list mode
          }
          
          // Check if this might be start of grocery list (has exactly 3 columns and first column looks like a category)
          if (values.length === 3 && !parsingGroceryList) {
            const possibleCategory = values[0].toLowerCase().trim();
            const groceryCategories = ['protein', 'vegetable', 'fruit', 'grain', 'dairy', 'fat', 'oil', 'condiment', 'spice', 'herb', 'nut', 'seed'];
            
            // Check if it looks like a grocery category
            if (groceryCategories.some(cat => possibleCategory.includes(cat))) {
              console.log('Detected grocery list start:', values[0]);
              parsingGroceryList = true;
              groceryList.push({
                category: values[0],
                item: values[1],
                total_amount: values[2]
              });
              continue;
            }
          }
          
          if (values.length < 10) {
            errors.push(`Line ${i + 1}: Not enough columns (${values.length}/10 minimum)`);
            continue;
          }

          // New CSV format: meal_type, recipe_name, ingredient_name, ingredient_count_display, grams, kcal, protein_g, carbs_g, fat_g, estimated_time, instructions
          const mealType = values[0];
          const recipeName = values[1];
          const ingredientName = values[2];
          const countDisplay = values[3];
          const grams = parseFloat(values[4]) || 0;
          const kcal = parseFloat(values[5]) || 0;
          const protein = parseFloat(values[6]) || 0;
          const carbs = parseFloat(values[7]) || 0;
          const fat = parseFloat(values[8]) || 0;
          const estimatedTime = values[9] || '';
          const instructions = values[10] || '';

          const recipeKey = `${mealType}_${recipeName}`;
          
          if (!recipes[recipeKey]) {
            recipes[recipeKey] = {
              day: null,  // Recipes are options, not tied to specific days
              meal_type: mealType,
              name: recipeName,
              instructions,
              estimated_time: estimatedTime,
              image_url: null,  // Images will be added by coach later
              ingredients: []
            };
          }

          recipes[recipeKey].ingredients.push({
            food_item_name: ingredientName,
            count_display: cleanCountDisplay(countDisplay, ingredientName),
            grams,
            kcal,
            protein_g: protein,
            carbs_g: carbs,
            fat_g: fat
          });

          totalCalories += kcal;
        } catch (err) {
          errors.push(`Line ${i + 1}: ${err.message}`);
        }
      }

      console.log('Parsed grocery list items:', groceryList.length);

      if (errors.length > 0) {
        console.error('CSV Errors:', errors);
      }

      const recipesArray = Object.values(recipes);
      
      if (recipesArray.length === 0) {
        toast.error('No valid recipes found in CSV');
        setUploadingCSV(false);
        return;
      }

      // Use recommended calories from onboarding as target calories
      let targetCalories = 2000; // Default fallback
      
      if (onboardingResponse && onboardingResponse.answers_json) {
        // Calculate recommended calories from onboarding data (TDEE-based)
        const answers = onboardingResponse.answers_json;
        const age = parseInt(answers.age) || 25;
        const weight = parseFloat(answers.weight) || 70;
        const height = parseFloat(answers.height) || 170;
        const gender = answers.gender || 'Male';
        const activityLevel = answers.activity_level || 'Moderate';
        const goal = answers.goal || 'Maintenance';
        
        // Calculate BMR using Mifflin-St Jeor equation
        let bmr;
        if (gender === 'Male') {
          bmr = 10 * weight + 6.25 * height - 5 * age + 5;
        } else {
          bmr = 10 * weight + 6.25 * height - 5 * age - 161;
        }
        
        // Activity multipliers
        const activityMultipliers = {
          'Sedentary': 1.2,
          'Light': 1.375,
          'Moderate': 1.55,
          'Very Active': 1.725,
          'Extra Active': 1.9
        };
        
        const tdee = bmr * (activityMultipliers[activityLevel] || 1.55);
        
        // Goal adjustments
        let recommendedCalories = tdee;
        if (goal === 'Weight Loss') {
          recommendedCalories = tdee * 0.85; // 15% deficit
        } else if (goal === 'Weight Gain') {
          recommendedCalories = tdee * 1.1; // 10% surplus
        }
        
        targetCalories = Math.round(recommendedCalories);
        console.log(`Using onboarding recommended calories: ${targetCalories} kcal/day (TDEE: ${Math.round(tdee)}, Goal: ${goal})`);
        toast.success(`Target calories set to ${targetCalories} kcal/day from onboarding`);
      } else {
        console.log('No onboarding data found, using default 2000 kcal');
        toast.info('Using default 2000 kcal (complete onboarding for personalized calories)');
      }

      console.log('Parsed recipes:', recipesArray.length);
      console.log('Target calories:', targetCalories);

      // Upload meal plan
      await axios.post(`${API}/meal-plans/${clientId}`, {
        recipes: recipesArray,
        target_calories: targetCalories,
        grocery_list: groceryList
      });

      toast.success(`Meal plan uploaded! ${recipesArray.length} recipes added.`);
      fetchClientData();
    } catch (error) {
      console.error('CSV Upload Error:', error);
      
      // Handle FastAPI validation errors properly
      let errorMessage = 'Failed to upload meal plan';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Extract error messages from validation error array
          errorMessage = detail.map(err => {
            const location = err.loc ? err.loc.join(' → ') : '';
            return `${location}: ${err.msg}`;
          }).join('; ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setUploadingCSV(false);
    }
  };

  const handleTextPaste = async (text, targetCalories) => {
    setUploadingCSV(true);

    try {
      // Parse the text to extract recipes
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('Text is empty or invalid');
        setUploadingCSV(false);
        return;
      }

      // Simple parsing for ChatGPT format - look for meal types and recipes
      const recipes = {};
      let currentMealType = null;
      let currentRecipe = null;
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        
        // Skip empty lines
        if (!trimmedLine) continue;
        
        // Check if this is a meal type header (Breakfast, Lunch, etc.)
        if (trimmedLine.match(/^(Breakfast|Lunch|Dinner|Snack)/i)) {
          currentMealType = trimmedLine.replace(':', '').trim();
          continue;
        }
        
        // Check if this looks like a recipe name (not an ingredient)
        if (currentMealType && !trimmedLine.startsWith('-') && !trimmedLine.startsWith('•') && !trimmedLine.match(/^\d/)) {
          currentRecipe = {
            meal_type: currentMealType,
            name: trimmedLine,
            instructions: 'Instructions from ChatGPT',
            image_url: null,
            ingredients: []
          };
          
          const recipeKey = `${currentMealType}_${trimmedLine}`;
          recipes[recipeKey] = currentRecipe;
        }
        
        // Parse ingredients (lines starting with - or • or numbers)
        if (currentRecipe && (trimmedLine.startsWith('-') || trimmedLine.startsWith('•') || trimmedLine.match(/^\d/))) {
          const ingredientText = trimmedLine.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '');
          
          // Basic ingredient parsing - this is a simple version
          currentRecipe.ingredients.push({
            food_item_name: ingredientText,
            count_display: '1 serving',
            grams: 100, // Default values - user can edit later
            kcal: 100,
            protein_g: 5,
            carbs_g: 10,
            fat_g: 3
          });
        }
      }

      const recipesArray = Object.values(recipes);
      
      if (recipesArray.length === 0) {
        toast.error('No valid recipes found in text. Please check the format.');
        setUploadingCSV(false);
        return;
      }

      // Upload meal plan with default nutritional values
      await axios.post(`${API}/meal-plans/${clientId}`, {
        recipes: recipesArray,
        target_calories: targetCalories
      });

      toast.success(`Meal plan created! ${recipesArray.length} recipes added. Please review and edit nutritional values.`);
      fetchClientData();
    } catch (error) {
      console.error('Text Parse Error:', error);
      
      let errorMessage = 'Failed to create meal plan from text';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            const location = err.loc ? err.loc.join(' → ') : '';
            return `${location}: ${err.msg}`;
          }).join('; ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setUploadingCSV(false);
    }
  };

  if (loading) {
    return <div data-testid="loading">Loading...</div>;
  }

  if (!client) {
    return <div>Client not found</div>;
  }

  const latestWeight = weightLogs?.[0]?.weight;
  const weightChartData = (weightLogs || []).slice(0, 30).reverse().map(log => ({
    date: log.date,
    weight: log.weight
  }));

  // Calculate adherence scores from check-ins
  const calculateAdherence = () => {
    if (!checkins || checkins.length === 0) {
      return { recent: null, average: null, recentDate: null };
    }

    // Sort check-ins by date (most recent first)
    const sortedCheckins = [...checkins].sort((a, b) => 
      new Date(b.created_at || b.submitted_at) - new Date(a.created_at || a.submitted_at)
    );

    // Recent score: latest check-in average of consistency, hunger, energy
    const recent = sortedCheckins[0];
    const recentScore = recent ? 
      ((recent.consistency || 0) + (recent.hunger || 0) + (recent.energy || 0)) / 3 : null;
    const recentDate = recent ? (recent.created_at || recent.submitted_at) : null;

    // Average score: all check-ins average
    const allScores = sortedCheckins.map(c => 
      ((c.consistency || 0) + (c.hunger || 0) + (c.energy || 0)) / 3
    );
    const average = allScores.length > 0 ? 
      allScores.reduce((sum, score) => sum + score, 0) / allScores.length : null;

    return { 
      recent: recentScore ? recentScore.toFixed(1) : null, 
      average: average ? average.toFixed(1) : null,
      recentDate 
    };
  };

  // Calculate weight summary
  const calculateWeightSummary = () => {
    const starting = onboardingResponse?.answers_json?.weight || null;
    const current = latestWeight || null;
    const change = (starting && current) ? (current - starting).toFixed(1) : null;
    
    // Calculate weekly average weight loss
    let weeklyAvg = null;
    let weeksElapsed = null;
    
    if (starting && current && onboardingResponse?.created_at) {
      const startDate = new Date(onboardingResponse.created_at);
      const currentDate = new Date();
      const daysElapsed = (currentDate - startDate) / (1000 * 60 * 60 * 24);
      weeksElapsed = Math.floor(daysElapsed / 7);
      
      // Only calculate if at least 2 weeks have passed
      if (weeksElapsed >= 2) {
        const totalChange = current - starting;
        weeklyAvg = (totalChange / weeksElapsed).toFixed(2);
        
        // Sanity check: weekly change shouldn't be more than 2kg (unrealistic)
        if (Math.abs(parseFloat(weeklyAvg)) > 2) {
          weeklyAvg = null; // Data might be wrong
        }
      }
    }
    
    return { starting, current, change, weeklyAvg, weeksElapsed };
  };

  const adherence = calculateAdherence();
  const weightSummary = calculateWeightSummary();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="page-container">
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="client-profile-title">
                {client.first_name} {client.last_name}
              </h1>
              <p className="text-lg text-gray-600">{client.email}</p>
              <p className="text-sm text-gray-500 mt-1">
                Viewing as coach - This shows all client data and management tools
              </p>
            </div>
            <Button
              onClick={() => navigate('/coach/messages', { state: { selectedClientId: clientId } })}
              className="flex items-center gap-2"
              size="lg"
            >
              <MessageSquare className="h-5 w-5" />
              Send Message
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList data-testid="profile-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="info" data-testid="tab-info">Client Info</TabsTrigger>
            <TabsTrigger value="checkins" data-testid="tab-checkins">Check-ins</TabsTrigger>
            <TabsTrigger value="weight" data-testid="tab-weight">Weight Log</TabsTrigger>
            <TabsTrigger value="mealplan" data-testid="tab-mealplan">Meal Plan</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Adherence & Weight Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Adherence Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    Adherence Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {adherence.recent ? (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Latest Score</p>
                        <p className="text-3xl font-bold text-blue-600">
                          {adherence.recent}/5
                        </p>
                        {adherence.recentDate && (
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(adherence.recentDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Average Score</p>
                        <p className="text-2xl font-semibold text-gray-700">
                          {adherence.average}/5
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Based on {checkins.length} check-ins
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No check-ins yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Weight Summary Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="h-5 w-5 text-purple-600" />
                    Weight Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {weightSummary.current ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Starting</p>
                          <p className="text-xl font-semibold text-gray-700">
                            {weightSummary.starting ? `${weightSummary.starting} kg` : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Current</p>
                          <p className="text-xl font-semibold text-purple-600">
                            {weightSummary.current} kg
                          </p>
                        </div>
                      </div>
                      {weightSummary.change && (
                        <div className="pt-3 border-t">
                          <p className="text-sm text-gray-600 mb-1">Total Change</p>
                          <p className={`text-2xl font-bold ${
                            parseFloat(weightSummary.change) < 0 ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            {weightSummary.change} kg
                          </p>
                        </div>
                      )}
                      {weightSummary.weeklyAvg && (
                        <div className="pt-3 border-t">
                          <p className="text-sm text-gray-600 mb-1">Weekly Average</p>
                          <p className={`text-xl font-bold ${
                            parseFloat(weightSummary.weeklyAvg) < 0 ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            {weightSummary.weeklyAvg} kg/week
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Over {weightSummary.weeksElapsed} weeks
                          </p>
                        </div>
                      )}
                      {!weightSummary.weeklyAvg && weightSummary.weeksElapsed !== null && weightSummary.weeksElapsed < 2 && (
                        <div className="pt-3 border-t">
                          <p className="text-sm text-gray-500 italic">
                            Weekly average available after 2 weeks
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No weight data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Weight Chart */}
            {weightChartData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Weight Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={weightChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="weight" stroke="#8b5cf6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Recent Check-ins */}
            {checkins.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Recent Check-ins</CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        // Switch to check-ins tab
                        const checkinsTab = document.querySelector('[value="checkins"]');
                        if (checkinsTab) checkinsTab.click();
                      }}
                    >
                      View All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {checkins.slice(0, 3).map((checkin, index) => {
                      const score = ((checkin.consistency || 0) + (checkin.hunger || 0) + (checkin.energy || 0)) / 3;
                      const date = new Date(checkin.created_at || checkin.submitted_at).toLocaleDateString();
                      
                      return (
                        <div 
                          key={checkin.id} 
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => {
                            const checkinsTab = document.querySelector('[value="checkins"]');
                            if (checkinsTab) checkinsTab.click();
                          }}
                        >
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{date}</p>
                            <div className="flex gap-4 mt-1 text-xs text-gray-600">
                              <span>Consistency: {checkin.consistency}/5</span>
                              <span>Energy: {checkin.energy}/5</span>
                              <span>Hunger: {checkin.hunger}/5</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Overall</p>
                            <p className="text-2xl font-bold text-blue-600">{score.toFixed(1)}/5</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Client Info Tab */}
          <TabsContent value="info" className="space-y-6">
            {onboardingResponse && (
              <Card>
                <CardHeader>
                  <CardTitle>Quick Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Starting Weight</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {onboardingResponse.answers_json.weight} kg
                      </p>
                    </div>
                    <div className="text-center p-4 bg-[#1a61e8]/5 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Current Weight</p>
                      <p className="text-2xl font-bold text-[#1a61e8]">
                        {latestWeight ? `${latestWeight} kg` : 'N/A'}
                      </p>
                    </div>
                    {(onboardingResponse?.answers_json?.goal_weight || client?.goal_weight) && (
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Goal Weight</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {client?.goal_weight || onboardingResponse.answers_json.goal_weight} kg
                        </p>
                      </div>
                    )}
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Height</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {onboardingResponse.answers_json.height} cm
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>General Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Full Name</p>
                    <p className="font-semibold">{client.first_name} {client.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold">{client.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-semibold capitalize">{client.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Joined</p>
                    <p className="font-semibold">{new Date(client.created_at).toLocaleDateString()}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Billing Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {subscription ? (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">Plan</p>
                        <p className="font-semibold">{subscription.plan}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <p className="font-semibold capitalize">{subscription.status}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Amount</p>
                        <p className="font-semibold">${subscription.amount}/month</p>
                      </div>
                      {subscription.started_at && (
                        <div>
                          <p className="text-sm text-gray-600">Started</p>
                          <p className="font-semibold">{new Date(subscription.started_at).toLocaleDateString()}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500">No billing information</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {onboardingResponse && (
              <Card>
                <CardHeader>
                  <CardTitle>Onboarding Responses</CardTitle>
                  <CardDescription>Initial assessment completed {new Date(onboardingResponse.submitted_at).toLocaleDateString()}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(onboardingResponse.answers_json).map(([key, value]) => (
                      <div key={key}>
                        <p className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="font-semibold">{Array.isArray(value) ? value.join(', ') : value}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Check-ins Tab */}
          <TabsContent value="checkins" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Weekly Check-ins History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4" data-testid="checkins-list">
                  {checkins.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No check-ins yet</p>
                  ) : (
                    checkins.map((checkin, index) => (
                      <div key={checkin.id} className="p-4 border rounded-lg" data-testid={`checkin-${index}`}>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="font-semibold">Week of {checkin.week_start_date}</h4>
                            <p className="text-sm text-gray-600">
                              Submitted: {new Date(checkin.submitted_at).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant="outline">{checkin.weight} kg</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Energy</p>
                            <p className="font-semibold">{checkin.energy}/5</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Hunger</p>
                            <p className="font-semibold">{checkin.hunger}/5</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Consistency</p>
                            <p className="font-semibold">{checkin.consistency}/5</p>
                          </div>
                        </div>
                        {checkin.recipe_change_requested && (
                          <div className="mt-4 p-3 bg-yellow-50 rounded">
                            <p className="font-semibold text-sm text-yellow-800">Recipe Change Requested</p>
                            <p className="text-sm text-gray-700 mt-1">{checkin.recipe_change_details}</p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Weight Log Tab */}
          <TabsContent value="weight" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Weight Log History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="weight-log-table">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Date</th>
                        <th className="text-right py-3 px-4">Weight (kg)</th>
                        <th className="text-right py-3 px-4">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weightLogs.map((log, index) => {
                        const prevWeight = weightLogs[index + 1]?.weight;
                        const change = prevWeight ? (log.weight - prevWeight).toFixed(1) : null;
                        
                        return (
                          <tr key={log.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">{log.date}</td>
                            <td className="py-3 px-4 text-right font-semibold">{log.weight}</td>
                            <td className="py-3 px-4 text-right">
                              {change !== null && (
                                <span className={change > 0 ? 'text-red-600' : 'text-[#1a61e8]'}>
                                  {change > 0 ? '+' : ''}{change}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Meal Plan Tab */}
          <TabsContent value="mealplan" className="space-y-6">
            {/* Meal Plan Selector */}
            {allMealPlans.length > 1 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">Manage Meal Plans</h3>
                      <p className="text-sm text-gray-600">{allMealPlans.length} plan{allMealPlans.length > 1 ? 's' : ''} available</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {allMealPlans.map((plan, index) => (
                      <div
                        key={plan.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition ${
                          selectedPlanId === plan.id 
                            ? 'border-[#1a61e8] bg-[#1a61e8]/5' 
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <Button
                          variant={selectedPlanId === plan.id ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setSelectedPlanId(plan.id);
                            setMealPlan(plan);
                          }}
                          className={selectedPlanId === plan.id ? "bg-[#1a61e8]" : ""}
                        >
                          {selectedPlanId === plan.id ? 'Viewing' : 'View'}
                        </Button>
                        
                        <div className="flex-1">
                          <div className="font-semibold text-sm">
                            {plan.plan_name || `Meal Plan ${index + 1}`}
                            {plan.plan_type === 'simplified' && ' (Simple)'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {Math.round(plan.target_calories)} kcal/day • {plan.recipes?.length || 0} recipes
                          </div>
                        </div>
                        
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={async () => {
                            if (window.confirm(`Delete "${plan.plan_name || `Meal Plan ${index + 1}`}"? This cannot be undone.`)) {
                              try {
                                await axios.delete(`${API}/meal-plans/${plan.id}`);
                                toast.success('Meal plan deleted');
                                fetchClientData();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to delete meal plan');
                              }
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {!mealPlan ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center mb-6">
                    <Utensils className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-4">No Active Meal Plan</h3>
                    <p className="text-gray-600 mb-6">Create a customized meal plan for this client</p>
                  </div>

                  <div className="max-w-md mx-auto">
                    <Button 
                      onClick={() => setRegenerateDialogOpen(true)} 
                      className="w-full bg-[#1a61e8] hover:bg-[#1a61e8]/90"
                      size="lg"
                    >
                      <Plus className="mr-2 h-5 w-5" />
                      Assign Meal Plan
                    </Button>
                    <p className="text-sm text-gray-500 text-center mt-3">
                      Choose meals per day, cooking time, dietary preferences, and more
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Regenerate Button - Prominent at Top */}
                <div className="mb-6 flex justify-between items-center bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border-2 border-blue-200">
                  <div>
                    <h3 className="font-bold text-lg text-gray-800">Current Meal Plan Active</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {mealPlan?.meals_per_day || 3} meals/day • {Math.round(mealPlan?.target_calories || 0)} kcal/day
                    </p>
                  </div>
                  <Button 
                    onClick={() => setRegenerateDialogOpen(true)}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold"
                    size="lg"
                  >
                    <RefreshCw className="mr-2 h-5 w-5" />
                    Regenerate Plan
                  </Button>
                </div>

                {/* Use New Coach Meal Plan View Component */}
                <CoachMealPlanView 
                  mealPlan={mealPlan} 
                  clientId={clientId}
                  onPlanUpdate={fetchClientData}
                />
              </>
            )}
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity" className="space-y-6" data-testid="activity-log-tab">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  Activity Log
                </CardTitle>
                <CardDescription>
                  Where this client has been clicking, and when. Doesn't include check-ins or weight logs - you already see those live.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activityLoading ? (
                  <p className="text-gray-500 text-sm">Loading...</p>
                ) : (
                  <>
                    <div className="mb-4 pb-4 border-b">
                      <span className="text-sm text-gray-600">Last active: </span>
                      <span className="font-semibold" data-testid="activity-last-active">
                        {timeAgo(activityLog.last_active)}
                      </span>
                    </div>
                    {activityLog.entries.length === 0 ? (
                      <p className="text-center py-8 text-gray-500" data-testid="activity-log-empty">
                        No activity recorded yet
                      </p>
                    ) : (
                      <div className="space-y-2" data-testid="activity-log-entries">
                        {activityLog.entries.map((entry) => (
                          <div
                            key={entry.id}
                            className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 border-b last:border-b-0"
                          >
                            <span className="text-sm text-gray-800">{entry.label}</span>
                            <span className="text-xs text-gray-500">{timeAgo(entry.timestamp)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Recipe Dialog */}
      <Dialog open={editingRecipe !== null} onOpenChange={() => {
        setEditingRecipe(null);
        setEditRecipeData(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Recipe</DialogTitle>
            <DialogDescription>Modify recipe details and ingredients</DialogDescription>
          </DialogHeader>
          
          {editRecipeData && (
            <div className="space-y-4">
              {/* Scale Recipe Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <Label className="text-sm font-semibold mb-2 block">Scale Entire Recipe</Label>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Target Calories</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 500"
                      id="target-calories-input"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={async () => {
                      const targetCal = parseFloat(document.getElementById('target-calories-input').value);
                      if (!targetCal || targetCal <= 0) {
                        toast.error('Enter valid target calories');
                        return;
                      }
                      
                      try {
                        // Use backend API with Client Accuracy logic
                        const response = await axios.post(
                          `${API}/recipes/${editingRecipe}/scale-to-calories`,
                          { target_calories: targetCal }
                        );
                        
                        const { scaled_ingredients, actual_calories, original_calories, adjustments } = response.data;
                        
                        // Update recipe with scaled ingredients
                        setEditRecipeData({
                          ...editRecipeData,
                          ingredients: scaled_ingredients
                        });
                        
                        // Clear input
                        document.getElementById('target-calories-input').value = '';
                        
                        // Show success message with adjustments info
                        let message = `Scaled from ${Math.round(original_calories)} to ${Math.round(actual_calories)} kcal`;
                        if (adjustments && adjustments.length > 0) {
                          message += ` (${adjustments.length} ingredient(s) rounded for Client Accuracy)`;
                        }
                        toast.success(message);
                        
                      } catch (error) {
                        console.error('Scaling error:', error);
                        const errorMsg = error.response?.data?.detail || 'Failed to scale recipe';
                        toast.error(errorMsg);
                      }
                    }}
                  >
                    Scale Recipe
                  </Button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Current total: {Math.round(editRecipeData.ingredients.reduce((sum, ing) => sum + (ing.kcal || 0), 0))} kcal
                </p>
              </div>

              {/* Recipe Name */}
              <div>
                <Label>Recipe Name</Label>
                <Input
                  value={editRecipeData.name}
                  onChange={(e) => setEditRecipeData({...editRecipeData, name: e.target.value})}
                />
              </div>

              {/* Instructions */}
              <div>
                <Label>Instructions</Label>
                <textarea
                  className="w-full border rounded p-2 min-h-[100px]"
                  value={editRecipeData.instructions}
                  onChange={(e) => setEditRecipeData({...editRecipeData, instructions: e.target.value})}
                />
              </div>

              {/* Recipe Totals */}
              <div className="bg-gray-50 border rounded-lg p-4">
                <Label className="text-sm font-semibold mb-3 block">Recipe Totals</Label>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-[#1a61e8]">
                      {Math.round(editRecipeData.ingredients.reduce((sum, ing) => sum + (ing.kcal || 0), 0))}
                    </p>
                    <p className="text-xs text-gray-600">Calories</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">
                      {Math.round(editRecipeData.ingredients.reduce((sum, ing) => sum + (ing.protein_g || 0), 0))}g
                    </p>
                    <p className="text-xs text-gray-600">Protein</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">
                      {Math.round(editRecipeData.ingredients.reduce((sum, ing) => sum + (ing.carbs_g || 0), 0))}g
                    </p>
                    <p className="text-xs text-gray-600">Carbs</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">
                      {Math.round(editRecipeData.ingredients.reduce((sum, ing) => sum + (ing.fat_g || 0), 0))}g
                    </p>
                    <p className="text-xs text-gray-600">Fat</p>
                  </div>
                </div>
              </div>

              {/* Ingredients - Structured Editor with Food Bank Integration */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="font-semibold text-base">Ingredients</Label>
                  <Button
                    onClick={() => setShowAddIngredient(true)}
                    variant="outline"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Ingredient
                  </Button>
                </div>
                <div className="space-y-4">
                  {editRecipeData.ingredients.map((ing, idx) => (
                    <div key={idx} className="border rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-12 gap-4 items-start">
                        {/* Food Bank Dropdown */}
                        <div className="col-span-5">
                          <Label className="text-xs text-gray-500">Ingredient</Label>
                          <Select
                            value={ing.food_bank_id || ''}
                            onValueChange={(value) => swapIngredient(idx, value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {foodBankItems.map(item => (
                                <SelectItem key={item.id} value={item.id}>
                                  {item.name} ({item.category})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!ing.food_bank_id && (
                            <p className="text-xs text-amber-600 mt-1 flex items-start gap-1">
                              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>Not linked to Food Bank. Please select an ingredient above.</span>
                            </p>
                          )}
                        </div>

                        {/* Grams Input */}
                        <div className="col-span-3">
                          <Label className="text-xs text-gray-500">Grams (editable)</Label>
                          <Input
                            type="number"
                            value={ing.grams}
                            onChange={(e) => updateIngredientGrams(idx, e.target.value)}
                            onBlur={(e) => updateIngredientGrams(idx, e.target.value)}
                            className="font-mono"
                            disabled={!ing.food_bank_id}
                          />
                          {ing.validation_warning && typeof ing.validation_warning === 'string' && (
                            <p className="text-xs text-amber-600 mt-1 flex items-start gap-1">
                              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>{ing.validation_warning}</span>
                            </p>
                          )}
                        </div>

                        {/* Unit Display (Helper) */}
                        <div className="col-span-4">
                          <Label className="text-xs text-gray-500">Unit Display</Label>
                          <div className="h-10 flex items-center text-sm text-gray-700 font-medium">
                            {ing.unit && ing.unit !== 'g' ? (
                              <span>≈ {ing.friendly_fraction || ing.quantity} {ing.unit}</span>
                            ) : (
                              <span>({Math.round(ing.grams)}g)</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Macros */}
                      <div className="flex items-center justify-between">
                        <div className="flex gap-4 text-xs text-gray-600">
                          <span>{Math.round(ing.kcal)} cal</span>
                          <span>P: {Math.round(ing.protein_g)}g</span>
                          <span>C: {Math.round(ing.carbs_g)}g</span>
                          <span>F: {Math.round(ing.fat_g)}g</span>
                        </div>

                        {/* Delete Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteIngredient(idx)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recipe Metadata - Auto-updated Allergens */}
              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-semibold">Allergens (Auto-detected)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {editRecipeData.allergens && editRecipeData.allergens.length > 0 ? (
                    editRecipeData.allergens.map((allergen, idx) => (
                      <Badge key={idx} variant="destructive" className="text-xs">
                        {allergen}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No allergens detected</p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ℹ️ Allergens update automatically when you add, remove, or swap ingredients
                </p>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setEditingRecipe(null);
                  setEditRecipeData(null);
                }}>
                  Cancel
                </Button>
                <Button onClick={handleSaveRecipe}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Ingredient Dialog */}
      <Dialog open={showAddIngredient} onOpenChange={setShowAddIngredient}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Ingredient</DialogTitle>
            <DialogDescription>
              Select an ingredient from the Food Bank and specify the amount
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Food Bank Selector */}
            <div>
              <Label>Ingredient</Label>
              <Select
                value={newIngredientFoodId}
                onValueChange={setNewIngredientFoodId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an ingredient..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {foodBankItems.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Grams Input */}
            <div>
              <Label>Amount (grams)</Label>
              <Input
                type="number"
                value={newIngredientGrams}
                onChange={(e) => setNewIngredientGrams(e.target.value)}
                placeholder="100"
                min="1"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddIngredient(false);
                  setNewIngredientFoodId('');
                  setNewIngredientGrams('100');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddIngredient}
                disabled={adding || !newIngredientFoodId}
              >
                <Plus className="w-4 h-4 mr-2" />
                {adding ? 'Adding...' : 'Add Ingredient'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recipe Swap Dialog */}
      <Dialog open={swapDialogOpen} onOpenChange={setSwapDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Swap Recipe</DialogTitle>
            <DialogDescription>
              {swappingRecipe && (
                <div className="mt-2">
                  <p className="font-medium text-gray-900">Current: {swappingRecipe.name}</p>
                  <p className="text-sm text-gray-600">{Math.round(swappingRecipe.total_kcal)} kcal • {swappingRecipe.meal_type}</p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {loadingSwapOptions ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Loading compatible recipes...</p>
              </div>
            ) : swapOptions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No compatible recipes found.</p>
                <p className="text-sm text-gray-500 mt-2">Try adjusting filters or add more recipes to your library.</p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Showing {swapOptions.length} compatible recipes with similar calories and matching dietary restrictions
                </p>
                <div className="grid grid-cols-1 gap-4">
                  {swapOptions.map((option) => (
                    <div
                      key={option.id}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-[#1a61e8]"
                      onClick={() => handleSwapRecipe(option.id)}
                    >
                      <div className="flex items-start gap-4">
                        {option.image_url && (
                          <img
                            src={fixImageUrl(option.image_url)}
                            alt={option.name}
                            className="w-20 h-20 object-contain rounded bg-gray-100"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="font-bold text-lg">{option.name}</h4>
                          <div className="flex gap-2 mt-2 flex-wrap">
                            <span className="text-xs bg-[#1a61e8]/10 text-[#1552c7] px-2 py-1 rounded">
                              {option.total_kcal} kcal
                            </span>
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              P: {option.total_protein}g
                            </span>
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                              C: {option.total_carbs}g
                            </span>
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                              F: {option.total_fat}g
                            </span>
                          </div>
                          {option.dietary_categories && option.dietary_categories.length > 0 && (
                            <div className="flex gap-1 mt-2 flex-wrap">
                              {option.dietary_categories.map((cat, idx) => (
                                <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full border">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="mt-2">
                            <p className="text-xs text-gray-600 font-medium">Ingredients:</p>
                            <p className="text-xs text-gray-500">
                              {option.ingredients.slice(0, 3).map(ing => ing.food_bank_name || ing.food_item_name).join(', ')}
                              {option.ingredients.length > 3 && ` +${option.ingredients.length - 3} more`}
                            </p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="self-center">
                          Select →
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setSwapDialogOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Regenerate/Assign Meal Plan Dialog */}
      <Dialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{mealPlan ? 'Regenerate Meal Plan' : 'Assign Meal Plan'}</DialogTitle>
            <DialogDescription>
              Customize parameters and generate a {mealPlan ? 'new' : ''} meal plan for this client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Meals Per Day - CORRECT VALUES: 3-6 */}
            <div>
              <Label htmlFor="meals_per_day">Meals Per Day</Label>
              <Select
                value={regenerateParams.meals_per_day}
                onValueChange={(value) => setRegenerateParams({...regenerateParams, meals_per_day: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 meals</SelectItem>
                  <SelectItem value="4">4 meals</SelectItem>
                  <SelectItem value="5">5 meals</SelectItem>
                  <SelectItem value="6">6 meals</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Diet Type (synced with onboarding) */}
            <div>
              <Label htmlFor="diet_type">Diet Type</Label>
              <Select
                value={regenerateParams.diet_type || 'standard'}
                onValueChange={(value) => setRegenerateParams({...regenerateParams, diet_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="vegetarian">Vegetarian</SelectItem>
                  <SelectItem value="vegan">Vegan</SelectItem>
                  <SelectItem value="pescatarian">Pescatarian</SelectItem>
                  <SelectItem value="keto">Keto</SelectItem>
                  <SelectItem value="halal">Halal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Distribution Profile - CORRECT VALUES */}
            <div>
              <Label htmlFor="distribution_profile">Meal Distribution</Label>
              <Select
                value={regenerateParams.distribution_profile}
                onValueChange={(value) => setRegenerateParams({...regenerateParams, distribution_profile: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="balanced">⚖️ Balanced</SelectItem>
                  <SelectItem value="big_breakfast">🌅 Big Breakfast</SelectItem>
                  <SelectItem value="big_lunch">🌞 Big Lunch</SelectItem>
                  <SelectItem value="big_dinner">🌙 Big Dinner</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Target Calories Override */}
            <div>
              <Label htmlFor="target_calories">Target Calories</Label>
              <input
                type="number"
                id="target_calories"
                placeholder="Enter target calories"
                value={regenerateParams.target_calories || ''}
                onChange={(e) => setRegenerateParams({
                  ...regenerateParams, 
                  target_calories: e.target.value ? parseInt(e.target.value) : null
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Pre-filled with current meal plan calories. Adjust to override.
              </p>
            </div>
            
            {/* Allergens Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                ℹ️ <strong>Allergens are automatically preserved</strong> from the client's profile. You don't need to re-select them.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setRegenerateDialogOpen(false)}
              disabled={regenerating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegenerateMealPlan}
              disabled={regenerating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {regenerating ? 'Generating...' : (mealPlan ? 'Regenerate Plan' : 'Assign Plan')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grocery List Modal */}
      <GroceryListModal
        open={showGroceryList}
        onOpenChange={setShowGroceryList}
        selectedRecipes={selectedRecipes}
        allRecipes={mealPlan?.recipes || []}
        foodBankItems={foodBankItems}
      />
    </div>
  );
}
